import { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, FileText, Download, Send, Eye, CheckCircle, Clock, Mail, FileCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '@/contexts/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContractsPage({ user, onLogout, isEmployee, permissions, employeeInfo }) {
  const { formatDate } = useSettings();
  const [contracts, setContracts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [previewContract, setPreviewContract] = useState(null);
  const [createMethod, setCreateMethod] = useState('template'); // 'template' or 'manual'
  const [newContract, setNewContract] = useState({
    booking_id: '',
    template_id: '',
    terms: '',
    services: [],
    payment_schedule: 'Deposit due at booking. Balance due 7 days before event.',
    content: ''
  });
  const [serviceInput, setServiceInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchContracts();
    fetchBookings();
    fetchTemplates();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API}/contracts`);
      setContracts(response.data);
    } catch (error) {
      console.error('Failed to fetch contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/contract-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleCreate = async () => {
    if (!newContract.booking_id) {
      toast.error('Please select a booking');
      return;
    }
    
    if (createMethod === 'template' && !newContract.template_id) {
      toast.error('Please select a template');
      return;
    }

    if (createMethod === 'manual' && newContract.services.length === 0) {
      toast.error('Please add at least one service');
      return;
    }

    try {
      await axios.post(`${API}/contracts`, newContract);
      toast.success('Contract created successfully');
      setShowDialog(false);
      resetForm();
      fetchContracts();
    } catch (error) {
      toast.error('Failed to create contract');
    }
  };

  const resetForm = () => {
    setNewContract({
      booking_id: '',
      template_id: '',
      terms: '',
      services: [],
      payment_schedule: 'Deposit due at booking. Balance due 7 days before event.',
      content: ''
    });
    setServiceInput('');
    setCreateMethod('template');
  };

  const addService = () => {
    if (serviceInput.trim()) {
      setNewContract({
        ...newContract,
        services: [...newContract.services, serviceInput.trim()]
      });
      setServiceInput('');
    }
  };

  const removeService = (index) => {
    setNewContract({
      ...newContract,
      services: newContract.services.filter((_, i) => i !== index)
    });
  };

  const downloadContract = async (contract) => {
    try {
      const response = await axios.get(`${API}/contracts/${contract.id}/download-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `contract-${contract.client_name || contract.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download contract');
    }
  };

  const sendContract = async (contractId) => {
    try {
      await axios.put(`${API}/contracts/${contractId}/send`);
      toast.success('Contract sent to client!');
      fetchContracts();
    } catch (error) {
      toast.error('Failed to send contract');
    }
  };

  const previewContractContent = (contract) => {
    setPreviewContract(contract);
    setShowPreviewDialog(true);
  };

  const getBookingInfo = (bookingId) => {
    return bookings.find(b => b.id === bookingId);
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/20 text-gray-400',
      sent: 'bg-neon-blue/20 text-neon-blue',
      viewed: 'bg-yellow-500/20 text-yellow-500',
      signed: 'bg-green-500/20 text-green-500',
      client_signed: 'bg-amber-500/20 text-amber-500',
      fully_signed: 'bg-green-500/20 text-green-500'
    };
    const icons = {
      draft: <FileText className="w-3 h-3 mr-1" />,
      sent: <Mail className="w-3 h-3 mr-1" />,
      viewed: <Eye className="w-3 h-3 mr-1" />,
      signed: <CheckCircle className="w-3 h-3 mr-1" />,
      client_signed: <CheckCircle className="w-3 h-3 mr-1" />,
      fully_signed: <CheckCircle className="w-3 h-3 mr-1" />
    };
    const labels = {
      draft: 'Draft',
      sent: 'Sent',
      viewed: 'Viewed',
      signed: 'Signed',
      client_signed: 'Client Signed',
      fully_signed: 'Fully Signed'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium flex items-center ${styles[status] || styles.draft}`}>
        {icons[status] || icons.draft}
        {labels[status] || status}
      </span>
    );
  };

  // Get bookings that don't have contracts yet
  const availableBookings = bookings.filter(
    booking => !contracts.some(c => c.booking_id === booking.id)
  );

  return (
    <Layout user={user} onLogout={onLogout} isEmployee={isEmployee} permissions={permissions} employeeInfo={employeeInfo}>
      <div data-testid="contracts-page">
        <div className="p-4 md:p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-heading text-4xl font-bold mb-2" data-testid="contracts-heading">Contracts</h1>
              <p className="text-muted-foreground">Generate and manage event contracts</p>
            </div>
            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="bg-neon-blue hover:bg-neon-blue/80 text-black" data-testid="create-contract-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  New Contract
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-contract-dialog">
                <DialogHeader>
                  <DialogTitle className="font-heading text-2xl text-foreground">Create New Contract</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-foreground">Select Booking *</Label>
                    <Select value={newContract.booking_id} onValueChange={(v) => setNewContract({ ...newContract, booking_id: v })}>
                      <SelectTrigger className="bg-background border-border text-foreground" data-testid="contract-booking-select">
                        <SelectValue placeholder="Choose a booking..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {availableBookings.length === 0 ? (
                          <SelectItem value="none" disabled>No bookings without contracts</SelectItem>
                        ) : (
                          availableBookings.map((booking) => (
                            <SelectItem key={booking.id} value={booking.id}>
                              {booking.client_name} - {formatDate(booking.event_date)} ({booking.event_type})
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <Tabs value={createMethod} onValueChange={setCreateMethod} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 bg-muted/30 mb-4">
                      <TabsTrigger value="template" data-testid="template-tab">Use Template</TabsTrigger>
                      <TabsTrigger value="manual" data-testid="manual-tab">Manual Entry</TabsTrigger>
                    </TabsList>

                    <TabsContent value="template">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Select Template *</Label>
                          <Select value={newContract.template_id} onValueChange={(v) => setNewContract({ ...newContract, template_id: v })}>
                            <SelectTrigger className="bg-background border-border text-foreground" data-testid="contract-template-select">
                              <SelectValue placeholder="Choose a template..." />
                            </SelectTrigger>
                            <SelectContent className="bg-card border-border">
                              {templates.length === 0 ? (
                                <SelectItem value="none" disabled>No templates available</SelectItem>
                              ) : (
                                templates.map((template) => (
                                  <SelectItem key={template.id} value={template.id}>
                                    {template.name} ({template.category})
                                  </SelectItem>
                                ))
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          The template variables will be automatically populated with booking data.
                        </p>
                      </div>
                    </TabsContent>

                    <TabsContent value="manual">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Services Included *</Label>
                          <div className="flex gap-2">
                            <Input
                              value={serviceInput}
                              onChange={(e) => setServiceInput(e.target.value)}
                              placeholder="e.g., Professional DJ Equipment"
                              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addService())}
                              className="bg-background border-border text-foreground"
                              data-testid="contract-service-input"
                            />
                            <Button onClick={addService} type="button" data-testid="add-service-btn">
                              Add
                            </Button>
                          </div>
                          <div className="space-y-2 mt-2">
                            {newContract.services.map((service, index) => (
                              <div key={index} className="flex items-center justify-between bg-muted/30 p-2 rounded-lg text-foreground" data-testid={`service-item-${index}`}>
                                <span>{service}</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => removeService(index)}
                                  className="text-red-500 hover:text-red-400"
                                  data-testid={`remove-service-btn-${index}`}
                                >
                                  Remove
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label className="text-foreground">Terms & Conditions</Label>
                          <textarea
                            value={newContract.terms}
                            onChange={(e) => setNewContract({ ...newContract, terms: e.target.value })}
                            className="w-full min-h-[120px] bg-background border border-border rounded-lg p-3 text-foreground"
                            placeholder="Enter contract terms..."
                            data-testid="contract-terms-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label className="text-foreground">Payment Schedule</Label>
                          <Input
                            value={newContract.payment_schedule}
                            onChange={(e) => setNewContract({ ...newContract, payment_schedule: e.target.value })}
                            className="bg-background border-border text-foreground"
                            data-testid="contract-payment-schedule-input"
                          />
                        </div>
                      </div>
                    </TabsContent>
                  </Tabs>

                  <Button
                    onClick={handleCreate}
                    className="w-full bg-neon-blue hover:bg-neon-blue/80 text-black"
                    data-testid="contract-create-submit-btn"
                  >
                    <FileCheck className="w-4 h-4 mr-2" />
                    Generate Contract
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          {/* Status Filter */}
          <div className="flex gap-2 mb-6" data-testid="contract-status-filter">
            {[
              { value: 'all', label: 'All' },
              { value: 'unsigned', label: 'Unsigned' },
              { value: 'signed', label: 'Signed' }
            ].map(f => {
              const isSigned = (c) => c.status === 'signed' || c.status === 'client_signed' || c.status === 'fully_signed';
              const count = f.value === 'all' ? contracts.length
                : f.value === 'signed' ? contracts.filter(isSigned).length
                : contracts.filter(c => !isSigned(c)).length;
              return (
                <button
                  key={f.value}
                  onClick={() => setStatusFilter(f.value)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                    statusFilter === f.value
                      ? 'bg-neon-blue text-black'
                      : 'bg-white/5 text-muted-foreground hover:bg-white/10'
                  }`}
                  data-testid={`filter-${f.value}`}
                >
                  {f.label}
                  <span className="ml-1.5 opacity-70">{count}</span>
                </button>
              );
            })}
          </div>

          {loading ? (
            <div className="text-center py-12">Loading...</div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-12 glass-card rounded-3xl" data-testid="no-contracts-message">
              <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading text-xl font-semibold mb-2">No Contracts Yet</h3>
              <p className="text-muted-foreground mb-4">Create your first contract to get started</p>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="contracts-list">
              {contracts
                .filter(c => {
                  if (statusFilter === 'all') return true;
                  const isSigned = c.status === 'signed' || c.status === 'client_signed' || c.status === 'fully_signed';
                  return statusFilter === 'signed' ? isSigned : !isSigned;
                })
                .map((contract) => {
                const booking = getBookingInfo(contract.booking_id);
                return (
                  <div
                    key={contract.id}
                    className="glass-card-hover rounded-2xl p-6"
                    data-testid={`contract-item-${contract.id}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-semibold mb-1">
                          {booking ? booking.client_name : 'Unknown Client'}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {booking ? `${booking.event_type} at ${booking.venue}` : 'N/A'}
                        </p>
                        {contract.template_name && (
                          <p className="text-xs text-neon-purple mt-1">
                            Template: {contract.template_name}
                          </p>
                        )}
                      </div>
                      {getStatusBadge(contract.status)}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                      <div>
                        <span className="block text-xs text-muted-foreground">Event Date</span>
                        <span className="text-foreground font-medium">
                          {booking ? formatDate(booking.event_date) : 'N/A'}
                        </span>
                      </div>
                      <div>
                        <span className="block text-xs text-muted-foreground">Created</span>
                        <span className="text-foreground">{formatDate(contract.created_at)}</span>
                      </div>
                      {contract.sent_at && (
                        <div>
                          <span className="block text-xs text-muted-foreground">Sent</span>
                          <span className="text-foreground">{formatDate(contract.sent_at)}</span>
                        </div>
                      )}
                      {contract.signed_at && (
                        <div>
                          <span className="block text-xs text-muted-foreground">Signed</span>
                          <span className="text-green-500 font-medium">{formatDate(contract.signed_at)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                      {contract.content && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => previewContractContent(contract)}
                          className="border-border hover:bg-muted/50"
                          data-testid={`preview-contract-btn-${contract.id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Preview
                        </Button>
                      )}
                      <Button
                        onClick={() => downloadContract(contract)}
                        variant="outline"
                        size="sm"
                        className="border-border hover:bg-muted/50"
                        data-testid={`download-contract-btn-${contract.id}`}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download PDF
                      </Button>
                      {contract.status === 'draft' && (
                        <Button
                          onClick={() => sendContract(contract.id)}
                          size="sm"
                          className="bg-neon-purple hover:bg-neon-purple/80 text-white"
                          data-testid={`send-contract-btn-${contract.id}`}
                        >
                          <Send className="w-4 h-4 mr-2" />
                          Send to Client
                        </Button>
                      )}
                      {contract.status === 'sent' && (
                        <span className="text-xs text-muted-foreground flex items-center">
                          <Clock className="w-3 h-3 mr-1" />
                          Awaiting signature
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Preview Dialog */}
          <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
            <DialogContent className="bg-card border border-border max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-foreground">
                  Contract Preview
                </DialogTitle>
              </DialogHeader>
              <div className="mt-4">
                <div className="bg-white text-black p-8 rounded-lg prose max-w-none">
                  {previewContract?.content ? (
                    <div dangerouslySetInnerHTML={{ __html: previewContract.content }} />
                  ) : (
                    <p>No content available</p>
                  )}
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </Layout>
  );
}
